import { useState, useEffect } from "react";
import { Plus, Trash2, UserCheck, Users, Phone, Pencil, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API } from "@/lib/api";

const TEAMS = ["Relations Manager", "Payee"];

const TEAM_COLORS = {
  "Relations Manager": "bg-blue-50 text-blue-700 border-blue-200",
  "Payee": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const StaffMaster = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ full_name: "", team: "", id: "", phone: "" });
  const [editingPhone, setEditingPhone] = useState(null);
  const [editPhoneValue, setEditPhoneValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [teamFilter, setTeamFilter] = useState("all");

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/staff`);
      setStaff(await res.json());
    } catch {
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setSelected(null);
    setFormData({ full_name: "", team: "", id: "", phone: "" });
    setDialogOpen(true);
  };

  const openDelete = (member) => { setSelected(member); setDeleteDialogOpen(true); };

  const handleSave = async () => {
    if (!formData.full_name.trim()) { toast.error("Full name is required"); return; }
    if (!formData.team) { toast.error("Please select a team"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/staff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error();
      toast.success("Staff member added!");
      setDialogOpen(false);
      fetchStaff();
    } catch {
      toast.error("Failed to add staff member");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API}/staff/${selected.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      toast.success("Staff member deleted");
      setDeleteDialogOpen(false);
      setSelected(null);
      fetchStaff();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const savePhone = async (memberId) => {
    try {
      const res = await fetch(`${API}/staff/${memberId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: editPhoneValue }),
      });
      if (!res.ok) throw new Error();
      toast.success("Phone number updated");
      setEditingPhone(null);
      fetchStaff();
    } catch {
      toast.error("Failed to update phone");
    }
  };

  const filtered = teamFilter === "all" ? staff : staff.filter(m => m.team === teamFilter);
  const rms = staff.filter(m => m.team === "Relations Manager");
  const payees = staff.filter(m => m.team === "Payee");

  return (
    <div className="space-y-6" data-testid="staff-master-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Staff Master</h1>
          <p className="text-slate-500 mt-1">Manage Relationship Managers and Payees</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0047FF] hover:bg-[#0033CC]" data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Staff
        </Button>
      </div>

      {/* Summary Cards */}
      {!loading && (
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-blue-100 bg-blue-50">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">Relationship Managers</p>
              <p className="text-2xl font-bold text-blue-700">{rms.length}</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-100 bg-emerald-50">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wide mb-1">Payees</p>
              <p className="text-2xl font-bold text-emerald-700">{payees.length}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        {["all", "Relations Manager", "Payee"].map(t => (
          <Button
            key={t}
            size="sm"
            variant={teamFilter === t ? "default" : "outline"}
            onClick={() => setTeamFilter(t)}
            className={teamFilter === t ? "bg-[#0047FF] hover:bg-[#0033CC]" : ""}
          >
            {t === "all" ? "All" : t}
          </Button>
        ))}
      </div>

      {/* Staff Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-pulse text-slate-500">Loading staff...</div>
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((member) => (
            <Card key={member.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`staff-card-${member.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-slate-900">{member.full_name}</CardTitle>
                      <p className="text-[10px] font-mono text-slate-400 mt-0.5 break-all">{member.id}</p>
                      <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full border ${TEAM_COLORS[member.team]}`}>
                        {member.team}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => openDelete(member)}
                    className="h-8 w-8 text-slate-400 hover:text-red-500"
                    data-testid={`delete-staff-${member.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-3 px-4">
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  {editingPhone === member.id ? (
                    <div className="flex items-center gap-1.5 flex-1">
                      <Input
                        value={editPhoneValue}
                        onChange={(e) => setEditPhoneValue(e.target.value)}
                        placeholder="Phone number"
                        className="h-7 text-xs flex-1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") savePhone(member.id);
                          if (e.key === "Escape") setEditingPhone(null);
                        }}
                      />
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => savePhone(member.id)}>
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditingPhone(null)}>
                        <X className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 flex-1">
                      {member.phone ? (
                        <a href={`tel:${member.phone}`} className="text-xs text-blue-600 hover:underline">{member.phone}</a>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No phone</span>
                      )}
                      <Button
                        size="icon" variant="ghost" className="h-6 w-6 ml-auto"
                        onClick={() => { setEditingPhone(member.id); setEditPhoneValue(member.phone || ""); }}
                      >
                        <Pencil className="w-3 h-3 text-slate-400" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-base font-medium text-slate-600 mb-2">No staff members yet</h3>
            <p className="text-slate-400 text-sm mb-6">Add your first RM or Payee</p>
            <Button onClick={openCreate} className="bg-[#0047FF] hover:bg-[#0033CC]">
              <Plus className="w-4 h-4 mr-2" /> Add Staff
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
            <DialogDescription>Enter their name and assign a team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))}
                placeholder="e.g. Rahul Sharma"
                data-testid="input-staff-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Custom ID</Label>
              <Input
                value={formData.id}
                onChange={(e) => setFormData(p => ({ ...p, id: e.target.value }))}
                placeholder="Leave blank to auto-generate"
                className="font-mono text-sm"
                data-testid="input-staff-id"
              />
              <p className="text-[11px] text-slate-400">Optional — set a custom identifier for this staff member.</p>
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                placeholder="e.g. 9876543210"
                data-testid="input-staff-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Team *</Label>
              <Select value={formData.team} onValueChange={(v) => setFormData(p => ({ ...p, team: v }))}>
                <SelectTrigger data-testid="select-staff-team">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {TEAMS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0047FF] hover:bg-[#0033CC]" data-testid="save-staff-btn">
              {saving ? "Saving..." : "Add Staff"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Staff Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selected?.full_name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
